/**
 * Security Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const SECURITY_ARTICLES_RU: HelpArticle[] = [
  {
    id: "sec-001",
    slug: "how-we-protect-your-funds",
    title: "Как мы защищаем ваши средства",
    description: "Меры безопасности для защиты ваших активов",
    category: "security",
    popular: true,
    order: 1,
    keywords: ["защита", "безопасность", "хранение", "страхование"],
    content: `
# Как мы защищаем ваши средства

Ваша безопасность — наш главный приоритет. Вот как мы защищаем ваши средства.

## Безопасность активов

### Криптовалюта
- Ведущие кастодиальные партнёры
- Мультиподписные кошельки
- Холодное хранение резервов
- Регулярные аудиты безопасности

### Фиатная валюта
- В банках с FDIC-страхованием
- Сегрегированные клиентские счета
- Без смешивания средств
- Ежедневная сверка

## Безопасность платформы

### Инфраструктура
- Сертификация SOC 2 Type II
- Хостинг AWS с шифрованием
- SLA доступности 99.9%
- Мониторинг 24/7

### Безопасность приложения
- Сквозное шифрование
- Регулярное тестирование на проникновение
- Программа вознаграждений за баги
- Аудиты кода

## Безопасность аккаунта

### Аутентификация
- Без паролей (magic links)
- Двухфакторная аутентификация
- Верификация устройств
- Управление сессиями

### Контроль доступа
- Белый список IP (опционально)
- Белый список адресов вывода
- Период охлаждения при изменениях
- Уведомления об активности

## Соответствие требованиям

- Лицензия на денежные переводы (где требуется)
- AML/KYC-соответствие
- Проверка по санкциям
- Мониторинг транзакций
- Регуляторная отчётность

## Страхование

- Страхование от преступлений
- Киберстрахование
- Страхование ошибок и упущений
- Детали предоставляются по запросу

## Чего мы не делаем

Храним пароли (вход без пароля)
Храним ваши приватные ключи
Делимся данными без согласия
Обрабатываем подозрительные транзакции
    `.trim(),
  },
  {
    id: "sec-002",
    slug: "recognizing-phishing",
    title: "Как распознать фишинг",
    description: "Защита от фишинговых атак",
    category: "security",
    order: 2,
    keywords: ["фишинг", "мошенничество", "фейк", "атака"],
    content: `
# Как распознать фишинг

Научитесь распознавать и избегать фишинговых атак.

## Что такое фишинг?

Фишинг — когда мошенники пытаются:
- Выманить данные для входа
- Заставить отправить деньги на поддельные адреса
- Установить вредоносное ПО
- Получить личную информацию

## Типичные методы фишинга

### Поддельные письма
- Выглядят как от Supabank
- Срочный тон
- Подозрительные ссылки
- Запрос конфиденциальных данных

### Поддельные сайты
- Похожие домены
- Скопированный дизайн
- Просят войти
- Нет HTTPS

### Соцсети
- Фейковые аккаунты поддержки
- Запросы данных в личку
- Фейковые розыгрыши
- Подмена личности

### Звонки
- Представляются Supabank
- Просят коды подтверждения
- Давят на немедленные действия
- Угрожают блокировкой

## Как распознать фишинг

### Проверьте адрес отправителя
- Настоящий: @supabank.xyz
- Подозрительный: @supabank-support.com

### Проверьте ссылки (до клика)
- Наведите курсор, чтобы увидеть адрес
- Настоящий: supabank.xyz
- Подозрительный: supabank.xyz.fake.com

### Признаки опасности
⚠️ Срочный/угрожающий тон
⚠️ Ошибки в тексте
⚠️ Запрос паролей
⚠️ Неожиданные вложения
⚠️ Давление «действуй сейчас»

## Чего мы никогда не попросим

Supabank НИКОГДА не будет:
- Просить ваш пароль
- Запрашивать 2FA-коды по email
- Просить отправить крипту «для проверки»
- Угрожать блокировкой из-за неоплаты
- Связываться через соцсети для поддержки

## При подозрении на фишинг

1. Не кликайте по ссылкам
2. Не отвечайте и не давайте данных
3. Сообщите на security@supabank.xyz
4. Перешлите подозрительное письмо
5. Удалите сообщение
    `.trim(),
  },
  {
    id: "sec-003",
    slug: "reporting-suspicious-activity",
    title: "Сообщить о подозрительной активности",
    description: "Как сообщить о проблемах безопасности",
    category: "security",
    order: 3,
    keywords: ["сообщить", "подозрительно", "мошенничество", "уведомить"],
    content: `
# Сообщить о подозрительной активности

Заметили что-то странное? Вот как сообщить.

## О чём сообщать

### По вашему аккаунту
- Транзакции, которые вы не делали
- Вход с неизвестного места
- Изменённые настройки без вашего ведома
- Пропавшие средства

### Фишинг/мошенничество
- Подозрительные письма
- Фейковые сайты
- Подмена в соцсетях
- Мошеннические звонки

### Уязвимости безопасности
- Баги на сайте
- Проблемы приложения
- Слабые места в защите
- Утечка данных

## Как сообщить

### По аккаунту
**Срочно** (несанкционированный доступ):
1. Email: security@supabank.xyz
2. Укажите: email аккаунта, описание
3. Ответим в течение часа

**Несрочно**:
1. Email: support@supabank.xyz
2. Или через чат в приложении
3. Ответ в течение 24 часов

### Фишинг
1. Email: security@supabank.xyz
2. Тема: «Фишинг»
3. Приложите: скриншот или перешлите письмо
4. Расследуем немедленно

### Уязвимости
Программа вознаграждений:
1. Email: security@supabank.xyz
2. Опишите уязвимость
3. Укажите шаги для воспроизведения
4. Вознаграждение за подтверждённые уязвимости

## Что происходит после сообщения

1. **Немедленное подтверждение**
2. **Начало расследования**
3. **Защита аккаунта при необходимости**
4. **Регулярные обновления**
5. **Решение и объяснение**

## Защита аккаунта

Пока мы расследуем:
- Смените пароль email
- Включите/сбросьте 2FA
- Проверьте недавние транзакции
- Проверьте подключённые устройства
    `.trim(),
  },
  {
    id: "sec-004",
    slug: "lost-device-procedure",
    title: "Потеря устройства",
    description: "Что делать при потере доступа к устройству",
    category: "security",
    order: 4,
    keywords: ["потеря", "устройство", "телефон", "украли", "доступ"],
    content: `
# Потеря устройства

Потеряли телефон или компьютер? Вот что делать немедленно.

## Срочные действия

### 1. Защитите email
- Смените пароль почты
- Включите 2FA на почте
- Проверьте логи доступа

### 2. Отключите сессии Supabank
С другого устройства:
1. Войдите на supabank.xyz
2. Откройте Настройки > Безопасность
3. Нажмите «Выйти на всех устройствах»
4. Подтвердите через 2FA

### 3. Проверьте активность
- Проверьте недавние транзакции
- Ищите несанкционированные действия
- Просмотрите историю входов

## Если потеряно устройство с 2FA

### С резервными кодами
1. Откройте страницу входа
2. Введите email для magic link
3. На запрос 2FA нажмите «Использовать резервный код»
4. Введите код
5. Войдите и сбросьте 2FA

### Без резервных кодов
1. Напишите на support@supabank.xyz
2. Тема: «Восстановление 2FA — потеря устройства»
3. Укажите:
   - Email аккаунта
   - Полное имя
   - Дату рождения
   - Детали недавних транзакций
4. Пройдите верификацию личности
5. 2FA будет сброшена

## Профилактика

### Заранее
Сохраните резервные коды оффлайн
Используйте приложение-аутентификатор с облачным бэкапом
Защитите email максимально
Помните данные аккаунта

### Регулярно
Проверяйте активные сессии раз в месяц
Обновляйте данные для восстановления
Проверяйте, что резервные коды работают
Держите контактную информацию актуальной

## Сроки

| Действие | Время |
|----------|-------|
| Отключение сессий | Мгновенно |
| Проверка email | Мгновенно |
| Восстановление 2FA (с кодами) | 5 минут |
| Восстановление 2FA (поддержка) | 24–48 часов |
    `.trim(),
  },
  {
    id: "sec-005",
    slug: "api-key-security",
    title: "Безопасность API-ключей",
    description: "Лучшие практики управления API-ключами",
    category: "security",
    order: 5,
    keywords: ["api", "ключ", "интеграция", "разработчик", "секрет"],
    content: `
# Безопасность API-ключей

Защитите свои API-ключи и интеграции.

## Основы API-ключей

### Для чего нужны
- Аутентификация API-запросов
- Идентификация приложения
- Отслеживание использования и лимитов
- Контроль разрешений

### Типы ключей
- **Только чтение**: просмотр данных
- **Полный доступ**: чтение и запись
- **Ограниченный**: конкретные действия

## Лучшие практики безопасности

### Хранение
Используйте переменные окружения
Используйте менеджеры секретов (AWS Secrets, Vault)
Никогда не коммитьте в репозиторий
Шифруйте при хранении

### Доступ
Ограничьте круг имеющих доступ
Применяйте принцип минимальных привилегий
Регулярно проводите аудит доступа
Периодически меняйте ключи

### Использование
Только HTTPS
Реализуйте ограничение запросов
Отслеживайте аномалии
Логируйте API-вызовы

## Чего НЕ делать

Хардкодить ключи в исходном коде
Передавать ключи через email/чат
Использовать продакшн-ключи в разработке
Хранить в публичных репозиториях
Использовать одни ключи для разных приложений

## Ротация ключей

### Когда менять
- По расписанию (каждые 90 дней)
- После ухода сотрудника
- После инцидента безопасности
- При возможной компрометации

### Как менять
1. Создайте новый ключ в настройках
2. Обновите приложение
3. Тщательно протестируйте
4. Деактивируйте старый ключ
5. Следите за проблемами

## Если ключ скомпрометирован

1. **Немедленно отзовите ключ**
2. Сгенерируйте новый
3. Обновите приложение
4. Проверьте логи API
5. Сообщите на security@supabank.xyz

## Мониторинг

- Регулярно просматривайте логи API
- Настройте алерты по использованию
- Следите за необычными паттернами
- Отслеживайте частоту ошибок
    `.trim(),
  },
  {
    id: "sec-006",
    slug: "compliance-aml",
    title: "Комплаенс и AML",
    description: "Наша практика соблюдения требований",
    category: "security",
    order: 6,
    keywords: ["комплаенс", "aml", "регуляции", "kyc"],
    content: `
# Комплаенс и AML

Как мы соблюдаем регуляторные требования и предотвращаем финансовые преступления.

## Регуляторная база

### Лицензии
- Лицензии на денежные переводы (штаты США)
- Европейский комплаенс (в процессе)
- Регулярные аудиты и проверки

### Стандарты
- Соответствие BSA/AML
- Проверка по санкциям OFAC
- Отчётность FinCEN
- Требования по штатам

## Знай своего клиента (KYC)

### Зачем мы проверяем личность
- Регуляторное требование
- Предотвращение мошенничества
- Безопасность аккаунта
- Предотвращение финансовых преступлений

### Что мы проверяем
- Документ госообразца
- Совпадение селфи
- Адрес (расширенные уровни)
- Источник средств (большие объёмы)

## Противодействие отмыванию (AML)

### Мониторинг транзакций
- Проверка в реальном времени
- Анализ паттернов
- Оценка рисков
- Выявление подозрительной активности

### Что мы проверяем
- Санкционные списки (OFAC, ООН, ЕС)
- Политически значимые лица (PEP)
- Негативные упоминания в СМИ
- Юрисдикции высокого риска

## Ваши обязанности

### Обязательная информация
- Достоверные персональные данные
- Источник средств (при запросе)
- Цель бизнеса (бизнес-аккаунты)
- Бенефициарное владение

### Запрещённая деятельность
Отмывание денег
Финансирование терроризма
Обход санкций
Уклонение от налогов
Мошенничество

## Конфиденциальность и данные

### Что мы собираем
- Документы, удостоверяющие личность
- Записи транзакций
- Логи коммуникаций
- Информация об устройствах

### Как мы защищаем
- Зашифрованное хранение
- Ограниченный доступ
- Политики хранения
- Право на удаление (где разрешено)

## Вопросы?

Пишите на compliance@supabank.xyz по вопросам комплаенса.
    `.trim(),
  },
];
